import { useState } from 'react';
import { useNode } from '@craftjs/core';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import ColorSettings from '@/components/editor/settings/ColorSettings';
import MediaManager from '@/components/media-manager/MediaManager';
import useResponsiveValues, {
  ResponsiveValue,
} from '@/components/editor/hooks/useResponsiveValues';

export enum BackgroundRepeat {
  REPEAT = 'repeat',
  NO_REPEAT = 'no-repeat',
  REPEAT_X = 'repeat-x',
  REPEAT_Y = 'repeat-y',
}

export enum BackgroundPosition {
  CENTER = 'center',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum BackgroundSize {
  COVER = 'cover',
  CONTAIN = 'contain',
  INITIAL = 'initial',
  AUTO = 'auto',
  UNSET = 'unset',
}

export enum BackgroundAttachment {
  SCROLL = 'scroll',
  FIXED = 'fixed',
}

export interface Background {
  backgroundColor?: string | ResponsiveValue<string>;
  backgroundImage?: string | ResponsiveValue<string>;
  backgroundSize?: BackgroundSize;
  backgroundPosition?: BackgroundPosition;
  backgroundRepeat?: BackgroundRepeat;
  backgroundAttachment?:
    | BackgroundAttachment
    | ResponsiveValue<BackgroundAttachment>;
}

enum BackgroundType {
  COLOR = 'color',
  GRADIENT = 'gradient',
  IMAGE = 'image',
  VIDEO = 'video',
}

interface Props extends Background {
  disabledFields?: string[];
}

const BackgroundSettings = ({ disabledFields }: Props) => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const [backgroundType, setBackgroundType] = useState<BackgroundType>(
    BackgroundType.COLOR
  );

  const responsive = useResponsiveValues();

  return (
    <Stack spacing={2}>
      <FormControl fullWidth={true}>
        <ToggleButtonGroup
          size="small"
          color="primary"
          fullWidth
          exclusive
          value={backgroundType}
          onChange={(_, value) => {
            return setBackgroundType(value);
          }}
        >
          <ToggleButton value={BackgroundType.COLOR}>Color</ToggleButton>
          <ToggleButton value={BackgroundType.IMAGE}>Imagen</ToggleButton>
        </ToggleButtonGroup>
      </FormControl>
      {backgroundType === BackgroundType.COLOR && (
        <ColorSettings field="backgroundColor" />
      )}

      {backgroundType === BackgroundType.IMAGE && (
        <Stack spacing={2}>
          {!disabledFields?.includes('backgroundImage') && (
            <MediaManager
              value={responsive.getValue(props.backgroundImage)}
              onChange={(value: string[]) => {
                setProp((props: Props) => {
                  props.backgroundImage = responsive.setValue(
                    props?.backgroundImage,
                    value[0]
                  );
                });
              }}
              onDelete={() => {
                setProp((props: Props) => {
                  props.backgroundImage = undefined;
                });
              }}
            />
          )}

          <TextField
            label="Url"
            size="small"
            fullWidth
            value={responsive.getValue(props.backgroundImage)}
            onChange={(e) => {
              setProp((props: Props) => {
                props.backgroundImage = responsive.setValue(
                  props.backgroundImage,
                  e.target.value
                );
                return props;
              });
            }}
          />

          <FormControl fullWidth>
            <InputLabel id="background-size">Tamaño</InputLabel>
            <Select
              label="Tamaño"
              value={responsive.getValue(props.backgroundSize)}
              size="small"
              onChange={(event: any) => {
                setProp((props: Background) => {
                  props.backgroundSize = responsive.setValue(
                    props?.backgroundSize,
                    event.target.value
                  );
                  return props;
                });
              }}
            >
              <MenuItem value={BackgroundSize.AUTO}>Auto</MenuItem>
              <MenuItem value={BackgroundSize.COVER}>Cubrir</MenuItem>
              <MenuItem value={BackgroundSize.CONTAIN}>Contener</MenuItem>
              <MenuItem value={BackgroundSize.INITIAL}>Inicial</MenuItem>
              <MenuItem value={BackgroundSize.UNSET}>No definido</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="background-size">Repetición</InputLabel>
              <Select
                label="Repetición"
                value={responsive.getValue(props.backgroundRepeat)}
                size="small"
                onChange={(event: any) => {
                  setProp((props: Background) => {
                    props.backgroundRepeat = responsive.setValue(
                      props?.backgroundRepeat,
                      event.target.value
                    );
                  });
                }}
              >
                <MenuItem value={BackgroundRepeat.REPEAT}>Repetir</MenuItem>
                <MenuItem value={BackgroundRepeat.NO_REPEAT}>No Repetir</MenuItem>
                <MenuItem value={BackgroundRepeat.REPEAT_X}>
                  Repetir en X
                </MenuItem>
                <MenuItem value={BackgroundRepeat.REPEAT_Y}>
                  Repetir en Y
                </MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="background-position">Posición</InputLabel>
              <Select
                label="Posición"
                value={responsive.getValue(props.backgroundPosition)}
                size="small"
                onChange={(event: any) => {
                  setProp((props: Background) => {
                    props.backgroundPosition = responsive.setValue(
                      props?.backgroundPosition,
                      event.target.value
                    );
                  });
                }}
              >
                <MenuItem value={BackgroundPosition.CENTER}>Centro</MenuItem>
                <MenuItem value={BackgroundPosition.TOP}>Arriba</MenuItem>
                <MenuItem value={BackgroundPosition.BOTTOM}>Abajo</MenuItem>
                <MenuItem value={BackgroundPosition.LEFT}>Izquierda</MenuItem>
                <MenuItem value={BackgroundPosition.RIGHT}>Derecha</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={responsive.getValue<boolean>(
                      props.backgroundAttachment
                    )}
                    onChange={(e: any) => {
                      setProp((props: Background) => {
                        props.backgroundAttachment = responsive.setValue(
                          props?.backgroundAttachment,
                          e.target.checked
                            ? BackgroundAttachment.FIXED
                            : BackgroundAttachment.SCROLL
                        );
                        return props;
                      });
                    }}
                  />
                }
                label="Posición fija"
              />
            </FormGroup>
        </Stack>
      )}
    </Stack>
  );
};

export default BackgroundSettings;
